import { computed, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useProductCompleteStore } from '@/features/Product/stores/productComplete';
import { useProductBaseStore } from '@/features/Product/stores/productBase';
import { useProductStockStore } from '@/features/Product/stores/productStock';
import { pickWeighted } from '@/composables/pickWeighted';
import type { AddToCartItem } from '@/features/Cart/types/servicesShopifyCart';
import type {
  CheckoutProductSelection,
  ProductComplete,
  RandomisationDetails,
  RandomisationException,
  RandomisationOption,
} from '@/features/Product/types/models';

export const useRandomisation = (
  randomisationDetails: Ref<RandomisationDetails | undefined>,
  exceptionDetails?: Ref<RandomisationException | undefined>,
  otherCartProductHandles?: Ref<string[]>,
  excludedSkus?: Ref<string[]>,
) => {
  const { cachedDisplayProducts, cachedCompleteProducts } = storeToRefs(useProductCompleteStore());
  const { completeBaseProducts } = storeToRefs(useProductBaseStore());
  const { isProductSoldOut, getExactStock } = useProductStockStore();
  const productSources = computed(() => [
    ...completeBaseProducts.value,
    ...cachedCompleteProducts.value,
    ...cachedDisplayProducts.value,
  ]);

  const findPoolProduct = (sku: string): ProductComplete | undefined => {
    const productHandle = randomisationDetails.value?.productHandle;
    return productSources.value.find(
      (p) => p.attributes.sku === sku && (!productHandle || p.attributes.handle === productHandle),
    );
  };

  const availableOptions = computed(() => {
    const details = randomisationDetails.value;
    if (!details?.options?.length) return [];
    const excluded = excludedSkus?.value ?? [];
    return details.options.filter((option) => {
      if (excluded.includes(option.sku)) return false;
      const product = findPoolProduct(option.sku);
      return product && !isProductSoldOut(product);
    });
  });

  const isRandomisationEnabled = computed(
    () =>
      (!!randomisationDetails.value?.baseSku || !!randomisationDetails.value?.productHandle) &&
      availableOptions.value.length > 0,
  );

  const isExceptionEligible = computed(() => {
    const exception = exceptionDetails?.value;
    const handles = otherCartProductHandles?.value;
    if (!exception || !handles?.length) return false;
    return handles.every((handle) => exception.productHandles.includes(handle));
  });

  const isMysteryItem = ({ sku }: Pick<CheckoutProductSelection, 'sku'>): boolean => {
    const details = randomisationDetails.value;
    if (!details) return false;
    if (details.baseSku && sku.startsWith(details.baseSku)) return true;
    if (details.productHandle) {
      return productSources.value.some(
        (p) => p.attributes.sku === sku && p.attributes.handle === details.productHandle,
      );
    }
    return false;
  };

  const isPoolOption = (sku: string): boolean =>
    !!randomisationDetails.value?.options?.some((option) => option.sku === sku);

  const isDrawnItem = ({ sku }: Pick<CheckoutProductSelection, 'sku'>): boolean =>
    isPoolOption(sku) || isMysteryItem({ sku });

  const isPoolPlaceholder = (product: ProductComplete): boolean => {
    const { sku } = product.attributes;
    return !isPoolOption(sku) && isMysteryItem({ sku });
  };

  const getOptionStock = (sku: string): number => {
    const product = findPoolProduct(sku);
    return (product ? getExactStock(product.attributes.variantId) : undefined) ?? Infinity;
  };

  const getOptionCapacity = ({ sku, maxQuantity }: RandomisationOption): number =>
    Math.min(maxQuantity ?? 5, getOptionStock(sku));

  const getOptionLimit = ({ sku, maxQuantity }: RandomisationOption): number =>
    Math.min(maxQuantity ?? Infinity, getOptionStock(sku));

  const poolSlotCapacity = computed(() =>
    availableOptions.value.reduce((total, option) => total + getOptionLimit(option), 0),
  );

  const createOptionDrawer = (selectedCounts: Map<string, number>) => {
    const minUniqueProducts = randomisationDetails.value?.minUniqueProducts ?? 0;
    return (): RandomisationOption | undefined => {
      const getCount = (option: RandomisationOption) => selectedCounts.get(option.sku) ?? 0;
      const withCapacity = availableOptions.value.filter(
        (option) => getCount(option) < getOptionCapacity(option),
      );
      const unseen = withCapacity.filter((option) => !selectedCounts.has(option.sku));
      const preferred =
        selectedCounts.size < minUniqueProducts && unseen.length ? unseen : withCapacity;
      if (preferred.length) return pickWeighted(preferred);
      return pickWeighted(
        availableOptions.value.filter((option) => getCount(option) < getOptionLimit(option)),
      );
    };
  };

  const drawSlots = (
    item: CheckoutProductSelection,
    selectedCounts: Map<string, number>,
  ): { selections: CheckoutProductSelection[]; isResolved: boolean } => {
    const drawOption = createOptionDrawer(selectedCounts);
    const draws = new Map<string, { variantId: number; quantity: number }>();
    let isResolved = true;
    for (let drawIndex = 0; drawIndex < item.quantity; drawIndex++) {
      const resolvedOption = drawOption();
      const resolvedProduct = resolvedOption && findPoolProduct(resolvedOption.sku);
      if (!resolvedProduct) {
        isResolved = false;
        break;
      }

      selectedCounts.set(resolvedOption.sku, (selectedCounts.get(resolvedOption.sku) ?? 0) + 1);
      const existingDraw = draws.get(resolvedOption.sku);
      if (existingDraw) {
        existingDraw.quantity += 1;
      } else {
        draws.set(resolvedOption.sku, {
          variantId: resolvedProduct.attributes.variantId,
          quantity: 1,
        });
      }
    }

    const selections = [...draws.entries()].map(([sku, draw]) => ({
      ...item,
      sku,
      quantity: draw.quantity,
      id: `gid://shopify/ProductVariant/${draw.variantId}` as const,
    }));
    return { selections, isResolved };
  };

  const resolveBundleDetails = (
    bundleDetails: CheckoutProductSelection[],
  ): { selection: CheckoutProductSelection; isResolved: boolean }[] => {
    const details = randomisationDetails.value;
    if (!details || (!isRandomisationEnabled.value && !isExceptionEligible.value)) {
      return bundleDetails.map((selection) => ({
        selection,
        isResolved: !isMysteryItem(selection),
      }));
    }
    const selectedCounts = new Map<string, number>();

    return bundleDetails.flatMap((item) => {
      if (!isMysteryItem(item)) return [{ selection: item, isResolved: true }];
      if (isExceptionEligible.value && exceptionDetails?.value) {
        const { assignedSku } = exceptionDetails.value;
        const resolvedProduct = productSources.value.find(
          (p) => p.attributes.sku === assignedSku.sku,
        );
        if (resolvedProduct) {
          return [
            {
              selection: {
                ...item,
                sku: assignedSku.sku,
                quantity: assignedSku.quantity,
                id: `gid://shopify/ProductVariant/${resolvedProduct.attributes.variantId}` as const,
              },
              isResolved: true,
            },
          ];
        }
      }

      const { selections, isResolved } = drawSlots(item, selectedCounts);
      if (!selections.length) return [{ selection: item, isResolved }];
      return selections.map((selection) => ({ selection, isResolved }));
    });
  };

  const redrawBundleDetails = (
    bundleDetails: CheckoutProductSelection[],
    isTargeted: (selection: CheckoutProductSelection) => boolean,
  ): CheckoutProductSelection[] | null => {
    const firstTargetedIndex = bundleDetails.findIndex(isTargeted);
    const slotCount = bundleDetails
      .filter(isTargeted)
      .reduce((total, { quantity }) => total + quantity, 0);
    const selectedCounts = bundleDetails
      .filter((selection) => !isTargeted(selection) && isDrawnItem(selection))
      .reduce(
        (counts, { sku, quantity }) => counts.set(sku, (counts.get(sku) ?? 0) + quantity),
        new Map<string, number>(),
      );
    const { selections, isResolved } = drawSlots(
      { ...bundleDetails[firstTargetedIndex], quantity: slotCount },
      selectedCounts,
    );
    if (!isResolved) return null;
    return bundleDetails.flatMap((selection, index) => {
      if (index === firstTargetedIndex) return selections;
      return isTargeted(selection) ? [] : [selection];
    });
  };

  const redrawMysteryAtcObject = (
    items: AddToCartItem[],
    variantIds: number[],
  ): AddToCartItem[] => {
    if (!isRandomisationEnabled.value || isExceptionEligible.value) return [];
    const targetedIds = new Set(variantIds.map((id) => `gid://shopify/ProductVariant/${id}`));
    const isTargeted = (selection: CheckoutProductSelection) =>
      targetedIds.has(selection.id) && isDrawnItem(selection);
    const redrawnItems = items.map((item): AddToCartItem | null => {
      const customBundleDetails = item.properties?._custom_bundle_details;
      if (!customBundleDetails) return item;
      try {
        const bundleDetails: CheckoutProductSelection[] = JSON.parse(customBundleDetails);
        if (!bundleDetails.some(isTargeted)) return item;
        const redrawnDetails = redrawBundleDetails(bundleDetails, isTargeted);
        if (!redrawnDetails) return null;
        return {
          ...item,
          properties: {
            ...item.properties,
            _custom_bundle_details: JSON.stringify(redrawnDetails),
          },
        };
      } catch {
        return item;
      }
    });
    const isRedrawn = redrawnItems.some((item, index) => item !== items[index]);
    if (!isRedrawn || redrawnItems.some((item) => !item)) return [];
    return redrawnItems.filter((item): item is AddToCartItem => !!item);
  };

  const resolveSkuInBundleDetails = (
    bundleDetails: CheckoutProductSelection[],
  ): CheckoutProductSelection[] =>
    resolveBundleDetails(bundleDetails).map(({ selection }) => selection);

  const resolveRandomisationAtcObject = (items: AddToCartItem[]): AddToCartItem[] => {
    if (!randomisationDetails.value && !isExceptionEligible.value) return items;
    const resolvedItems = items.map((item) => {
      const customBundleDetails = item.properties?._custom_bundle_details;
      if (!customBundleDetails) return { item, isResolved: true };
      try {
        const resolvedDetails = resolveBundleDetails(JSON.parse(customBundleDetails));
        return {
          item: {
            ...item,
            properties: {
              ...item.properties,
              _custom_bundle_details: JSON.stringify(
                resolvedDetails.map(({ selection }) => selection),
              ),
            },
          },
          isResolved: resolvedDetails.every(({ isResolved }) => isResolved),
        };
      } catch {
        return { item, isResolved: true };
      }
    });
    if (resolvedItems.some(({ isResolved }) => !isResolved)) return [];
    return resolvedItems.map(({ item }) => item);
  };

  return {
    isRandomisationEnabled,
    availableOptions,
    isPoolPlaceholder,
    poolSlotCapacity,
    resolveSkuInBundleDetails,
    resolveRandomisationAtcObject,
    redrawMysteryAtcObject,
  };
};
